import type { Dispatch, PropsWithChildren, ReactElement } from 'react';
import { useReducer, createContext, useContext, useEffect } from 'react';
import followingReducer, {
  type FollowingState,
  type FollowingStateAction,
} from './reducers/followingReducer';
import { GET_EVENT_FOLLOWS } from '../views/following/graphql/getEventFollows';
import type {
  GetEventFollowsData,
  GetEventFollowsVariables,
} from '../views/following/graphql/getEventFollows';
import { useLazyQuery } from '@apollo/client';
import { useApp } from './AppProvider';
import { useSearchParams } from 'react-router-dom';

const defaultState: FollowingState = {
  following: [],
  focussed: undefined,
  followsBySubEventId: {},
  followsByResultCode: {},
};

type FollowingProviderCtx = [FollowingState, Dispatch<FollowingStateAction>];

const FollowingCtx = createContext<FollowingProviderCtx>([defaultState, () => null]);

const FollowingProvider = ({ children }: PropsWithChildren): ReactElement => {
  const [state, dispatch] = useReducer(followingReducer, defaultState);
  const [searchParams, setSearchParams] = useSearchParams();
  const [{ event, hasRacesWithPredictiveTracking }] = useApp();
  const { id: eventId } = event || {};

  const [getInitialFollows] = useLazyQuery<GetEventFollowsData, GetEventFollowsVariables>(
    GET_EVENT_FOLLOWS,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  // we only need this to run on initial page load.
  useEffect(() => {
    if (eventId && hasRacesWithPredictiveTracking) {
      const followsFromUrl = searchParams.getAll('follows');
      const queryParams = followsFromUrl
        .reduce((acc, follow) => {
          acc.append('mergeFollows', follow);
          return acc;
        }, new URLSearchParams())
        .toString();
      getInitialFollows({ variables: { eventId, queryParams } }).then(({ data }) => {
        const initialFollows = data?.getEventFollows.data || [];
        const fromUrl = new Set(followsFromUrl);
        const fromServer = new Set(initialFollows.map((follow) => follow.resultCode));
        const delta = fromServer.difference(fromUrl);
        const currentActive = searchParams.get('active');
        const isActiveValid = [...fromServer].some((code) => code === currentActive);
        const newActive = initialFollows[0]?.resultCode;

        delta.forEach((resultCode) => {
          searchParams.append('follows', resultCode);
        });

        dispatch({ type: 'initialize', payload: initialFollows });

        if ((!currentActive || !isActiveValid) && newActive) {
          searchParams.set('active', newActive);
          dispatch({ type: 'focus', payload: newActive });
        }

        if (currentActive && isActiveValid) {
          dispatch({ type: 'focus', payload: currentActive });
        }

        if (!newActive) {
          searchParams.delete('active');
        }

        setSearchParams(searchParams);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, hasRacesWithPredictiveTracking]);

  return <FollowingCtx.Provider value={[state, dispatch]}>{children}</FollowingCtx.Provider>;
};

export const useFollowing = (): FollowingProviderCtx =>
  useContext<FollowingProviderCtx>(FollowingCtx);

export default FollowingProvider;
